/**
 * Notification centre store — keeps the last 50 events so the bell
 * dropdown can replay what the operator just saw (or missed).
 *
 * Flow: `notify.success(...)` → toast (transient, bottom-right) + store
 * push (persistent, in the bell). Unread count drives the badge.
 */

import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

export type NotificationTone = 'success' | 'info' | 'warn' | 'danger'

export interface NotificationRecord {
  id: string
  tone: NotificationTone
  title: string
  description?: string
  at: string
  read: boolean
  /** Optional deep-link (e.g. '/customers/abc'). */
  href?: string
  /** Optional "source" label — the page/domain that produced it. */
  source?: string
}

interface NotificationsState {
  items: NotificationRecord[]
  push: (rec: Omit<NotificationRecord, 'id' | 'at' | 'read'>) => void
  markAllRead: () => void
  markRead: (id: string) => void
  clear: () => void
  unreadCount: () => number
}

const MAX = 50

export const useNotifications = create<NotificationsState>()(
  persist(
    (set, get) => ({
      items: [],
      push: (rec) =>
        set((s) => ({
          items: [
            {
              ...rec,
              id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
              at: new Date().toISOString(),
              read: false,
            },
            ...s.items,
          ].slice(0, MAX),
        })),
      markAllRead: () =>
        set((s) => ({ items: s.items.map((i) => ({ ...i, read: true })) })),
      markRead: (id) =>
        set((s) => ({
          items: s.items.map((i) => (i.id === id ? { ...i, read: true } : i)),
        })),
      clear: () => set({ items: [] }),
      unreadCount: () => get().items.filter((i) => !i.read).length,
    }),
    {
      name: 'admindash-v2-notifications',
      storage: createJSONStorage(() => localStorage),
      // Persist items + read state; nothing else.
      partialize: (s) => ({ items: s.items }),
    },
  ),
)
